import { Loader } from '@/shared/components/ui/loader';
import { cn } from '@/shared/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-12 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-gradient-to-r from-ruda-primary to-ruda-secondary text-primary-foreground shadow-sm shadow-primary/20 hover:from-ruda-primary-dark hover:to-ruda-secondary-dark hover:shadow-md hover:shadow-primary/25 active:opacity-95',
        /** RDS Pop-Up Primary — Grey-900 · radius 16 */
        solid:
          'rounded-16 bg-[#131927] text-white shadow-none hover:bg-[#131927]/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-primary/25 bg-background text-foreground shadow-sm hover:border-primary/50 hover:bg-accent hover:text-accent-foreground',
        secondary:
          'border border-transparent bg-ruda-grey-100 text-ruda-text-primary shadow-none hover:bg-ruda-grey-100/80',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:text-ruda-primary-dark hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-8 px-3 text-xs',
        lg: 'h-10 rounded-12 px-6',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={loading || props.disabled}
        {...props}
      >
        {loading ? (
          <>
            <Loader size="tiny" className="text-current" />
            처리 중...
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
